import React, { useEffect, useMemo, useRef, useState } from 'react';
import './tabs-light.css';
import { supabase } from '../../supabase';
import { loadSkills, getCurrentSkills } from '../skillData';
import { buildSkillBlock, buildProfileBlock, loadSkillProfile } from '../mirrorData';
import {
  GOAL_TYPES, GOAL_TYPE_BY_VALUE,
  loadAllJourneys, createJourney, deleteJourney,
  loadLetters, saveLetter, saveYearReview,
  generateThemes, generateLetter, generateYearReview,
  GARDEN_COACH_SYSTEM,
  getCurrentMonthIndex, monthLabel, monthShortLabel, formatMonthYYYYMM,
  letterStatusForMonth, parseYearReview, formatLetterDate,
} from '../gardenData';

const READY_TOKEN = '[READY_FOR_GARDEN_LETTER]';

const FALLBACK_QUESTIONS = [
  "What's one moment from this past month that felt fully alive?",
  "What didn't grow the way you hoped — and what do you make of that?",
  "Did anything surprise you about yourself this month?",
  "Where did you most want to be tending, but couldn't?",
  "If next month had one focus, what would it be?",
];

export default function Garden({ onNavigateToTab }) {
  // Resolved on mount
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState('friend');
  const [skills, setSkills] = useState([]);
  const [profile, setProfile] = useState(null);
  const [allJourneys, setAllJourneys] = useState([]);
  const [journey, setJourney] = useState(null);
  const [letters, setLetters] = useState([]);

  // Page load
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // View
  const [view, setView] = useState('home');
  const [genErrorMsg, setGenErrorMsg] = useState(null);
  const [errorStage, setErrorStage] = useState(null); // 'themes' | 'letter' | 'review'

  // Goal-setting state
  const [goalDrafts, setGoalDrafts] = useState([
    { type: 'career',  title: '', description: '' },
    { type: 'skill',   title: '', description: '' },
    { type: 'project', title: '', description: '' },
  ]);

  // Theme editing state
  const [draftThemes, setDraftThemes] = useState(null); // {1: {theme, description}, ...}

  // Monthly conversation state
  const [targetMonthIdx, setTargetMonthIdx] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);

  // Letter display state
  const [displayedLetter, setDisplayedLetter] = useState(null); // { month_index, letter_content, created_at }
  const [freshLetterMode, setFreshLetterMode] = useState(false); // true if just generated

  // Year review state
  const [yearReviewText, setYearReviewText] = useState(null);
  const [confirmingReset, setConfirmingReset] = useState(false);

  const messagesEndRef = useRef(null);
  const currentYear = new Date().getFullYear();
  const [activeYear, setActiveYear] = useState(currentYear);

  /* MOUNT */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const { data: authData } = await supabase.auth.getUser();
        const user = authData?.user;
        if (!mounted) return;
        if (!user) { setLoadError('Not signed in'); return; }
        const uid = user.id;
        const name = user.user_metadata?.full_name || user.email?.split('@')[0] || 'friend';
        setUserId(uid);
        setUserName(String(name).split(' ')[0]);

        const [skillsData, profileData, journeysData] = await Promise.all([
          loadSkills(uid).catch(() => []),
          loadSkillProfile(uid),
          loadAllJourneys(uid).catch(() => []),
        ]);
        if (!mounted) return;

        const current = getCurrentSkills(skillsData);
        setSkills(current);
        setProfile(profileData);
        setAllJourneys(journeysData);

        if (current.length === 0) {
          setView('no-skills');
          return;
        }

        const j = journeysData.find(x => x.year === currentYear) || journeysData[0] || null;
        if (j) {
          setJourney(j);
          setActiveYear(j.year);
          const lts = await loadLetters(j.id).catch(() => []);
          if (mounted) setLetters(lts);
        }
      } catch (err) {
        if (mounted) setLoadError(err.message || 'Could not load Garden');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    if (view === 'monthly-conversation') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isThinking, view]);

  // Re-load letters when active year (or journey) changes
  const switchToYear = async (year) => {
    const j = allJourneys.find(x => x.year === year);
    setActiveYear(year);
    if (j) {
      setJourney(j);
      try {
        const lts = await loadLetters(j.id);
        setLetters(lts);
      } catch (_) {
        setLetters([]);
      }
      setYearReviewText(j.year_review_text || null);
    } else {
      setJourney(null);
      setLetters([]);
      setYearReviewText(null);
    }
  };

  const skillBlock   = useMemo(() => buildSkillBlock(skills), [skills]);
  const profileBlock = useMemo(() => buildProfileBlock(profile), [profile]);
  const currentMonthIdx = useMemo(() => journey ? getCurrentMonthIndex(journey) : 1, [journey]);
  const yearReviewUnlocked = useMemo(
    () => letters.length === 12 && currentMonthIdx >= 12,
    [letters.length, currentMonthIdx]
  );

  /* GOAL SETTING */

  const updateGoal = (idx, field, value) => {
    setGoalDrafts(prev => prev.map((g, i) => i === idx ? { ...g, [field]: value } : g));
  };

  const validGoals = goalDrafts.filter(g => g.title.trim().length > 0);
  const canProceedGoals = validGoals.length >= 1;

  const startThemeGeneration = async () => {
    if (!canProceedGoals) return;
    setView('theme-generation');
    setGenErrorMsg(null);
    try {
      const today = new Date();
      const year = today.getFullYear();
      const start_month = today.getMonth() + 1;
      const themes = await generateThemes({
        year,
        start_month,
        goals: validGoals,
        skillBlock,
        profileBlock,
      });
      setDraftThemes(themes);
      setView('theme-review');
    } catch (err) {
      setErrorStage('themes');
      setGenErrorMsg(err.message || 'Could not generate themes.');
      setView('gen-error');
    }
  };

  const updateDraftTheme = (idx, field, value) => {
    setDraftThemes(prev => ({
      ...prev,
      [idx]: { ...(prev[idx] || {}), [field]: value },
    }));
  };

  const lockTheYear = async () => {
    try {
      const today = new Date();
      const year = today.getFullYear();
      const start_month = today.getMonth() + 1;
      const created = await createJourney({
        user_id: userId,
        year,
        start_month,
        goals: validGoals,
        themes: draftThemes,
      });
      setJourney(created);
      setAllJourneys(prev => [created, ...prev.filter(j => j.year !== year)]);
      setActiveYear(created.year);
      setLetters([]);
      setView('home');
    } catch (err) {
      setErrorStage('themes');
      setGenErrorMsg(err.message || 'Could not save the year.');
      setView('gen-error');
    }
  };

  /* MONTHLY CONVERSATION */

  const startMonthlyConversation = async (monthIdx) => {
    setTargetMonthIdx(monthIdx);
    setMessages([]);
    setChatInput('');
    setIsThinking(true);
    setView('monthly-conversation');
    try {
      const themeData = (journey.themes && (journey.themes[monthIdx] || journey.themes[String(monthIdx)])) || { theme: '', description: '' };
      const opening = `${GARDEN_COACH_SYSTEM(userName, monthLabel(journey, monthIdx), themeData.theme, themeData.description, journey.goals || [], skillBlock)}

The user's name is ${userName}. They have just begun a Garden session for ${monthLabel(journey, monthIdx)}.

Output your warm opening message ONLY. Brief, calm, theme-aware. Address them by name. Do NOT include the [READY_FOR_GARDEN_LETTER] token in your opening.`;
      const response = await fetch('/api/claude', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: opening, model: 'haiku' }),
      });
      const data = await response.json();
      const text = (data?.content || []).map(i => i.text || '').join('').trim();
      const cleaned = text.replace(READY_TOKEN, '').trim();
      setMessages([{
        role: 'assistant',
        content: cleaned || `Hi ${userName}. Settle in. As you sit with this month — ${themeData.theme || 'this season'} — what feels alive in you right now?`,
        id: Date.now(),
      }]);
    } catch (_) {
      setMessages([{
        role: 'assistant',
        content: `Hi ${userName}. Take a breath. As we tend to ${monthLabel(journey, monthIdx)}, tell me — what grew this month, even if it surprised you?`,
        id: Date.now(),
      }]);
    } finally {
      setIsThinking(false);
    }
  };

  const sendMonthlyMessage = async (userText) => {
    const turn = { role: 'user', content: userText, id: Date.now() };
    const next = [...messages, turn];
    setMessages(next);
    setChatInput('');
    setIsThinking(true);
    try {
      const themeData = (journey.themes && (journey.themes[targetMonthIdx] || journey.themes[String(targetMonthIdx)])) || { theme: '', description: '' };
      const transcript = next.map(m =>
        `${m.role === 'user' ? userName : 'Curx AI'}: ${m.content}`
      ).join('\n\n');
      const prompt = `${GARDEN_COACH_SYSTEM(userName, monthLabel(journey, targetMonthIdx), themeData.theme, themeData.description, journey.goals || [], skillBlock)}

CONVERSATION SO FAR:
${transcript}

Respond as Curx AI. Output ONLY your message, no JSON, no markdown, no labels.`;

      const response = await fetch('/api/claude', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, model: 'haiku' }),
      });
      const data = await response.json();
      const text = (data?.content || []).map(i => i.text || '').join('').trim();

      if (text.includes(READY_TOKEN)) {
        const cleaned = text.replace(READY_TOKEN, '').trim();
        if (cleaned) {
          setMessages(m => [...m, { role: 'assistant', content: cleaned, id: Date.now() + 1 }]);
        }
        setIsThinking(false);
        setTimeout(() => composeLetter(next), 1200);
      } else {
        setMessages(m => [...m, { role: 'assistant', content: text, id: Date.now() + 1 }]);
        setIsThinking(false);
      }
    } catch (_) {
      const userTurns = next.filter(m => m.role === 'user').length;
      if (userTurns >= 5) {
        setMessages(m => [...m, {
          role: 'assistant',
          content: 'I have what I need. Let me sit with this and write you a letter.',
          id: Date.now() + 1,
        }]);
        setIsThinking(false);
        setTimeout(() => composeLetter(next), 1200);
      } else {
        const fb = FALLBACK_QUESTIONS[Math.min(userTurns, FALLBACK_QUESTIONS.length - 1)];
        setMessages(m => [...m, { role: 'assistant', content: fb, id: Date.now() + 1 }]);
        setIsThinking(false);
      }
    }
  };

  const composeLetter = async (finalMessages) => {
    setView('generating-letter');
    setGenErrorMsg(null);
    try {
      const transcript = finalMessages.map(m =>
        `${m.role === 'user' ? userName : 'Curx AI'}: ${m.content}`
      ).join('\n\n');

      const text = await generateLetter({
        userName,
        journey,
        monthIdx: targetMonthIdx,
        transcript,
      });

      const monthStr = formatMonthYYYYMM(journey, targetMonthIdx);
      const referenced = {
        conversation: finalMessages.map(m => ({ role: m.role, content: m.content })),
        theme: (journey.themes && (journey.themes[targetMonthIdx] || journey.themes[String(targetMonthIdx)])) || null,
        generated_at: new Date().toISOString(),
      };

      const saved = await saveLetter({
        user_id: userId,
        journey_id: journey.id,
        month_index: targetMonthIdx,
        month: monthStr,
        letter_content: text,
        referenced_data: referenced,
      });

      setLetters(prev => {
        // Deduplicate (in case backfilling)
        const filtered = prev.filter(l => l.month_index !== targetMonthIdx);
        return [...filtered, saved].sort((a, b) => a.month_index - b.month_index);
      });
      setDisplayedLetter(saved);
      setFreshLetterMode(true);
      setView('letter-display');
    } catch (err) {
      setErrorStage('letter');
      setGenErrorMsg(err.message || 'Could not compose the letter.');
      setView('gen-error');
    }
  };

  /* LETTER DISPLAY (read mode) */

  const openLetter = (letter) => {
    setDisplayedLetter(letter);
    setFreshLetterMode(false);
    setView('letter-display');
  };

  /* YEAR REVIEW */

  const openYearReview = async () => {
    if (!journey) return;
    if (journey.year_review_text) {
      setYearReviewText(journey.year_review_text);
      setView('year-review');
      return;
    }
    setView('year-review-generating');
    setGenErrorMsg(null);
    try {
      const text = await generateYearReview({ userName, journey, letters });
      const updated = await saveYearReview(journey.id, text);
      setJourney(updated);
      setAllJourneys(prev => prev.map(j => j.id === updated.id ? updated : j));
      setYearReviewText(text);
      setView('year-review');
    } catch (err) {
      setErrorStage('review');
      setGenErrorMsg(err.message || 'Could not generate the year review.');
      setView('gen-error');
    }
  };

  /* RESET */

  const requestReset = () => setConfirmingReset(true);

  const confirmReset = async () => {
    if (!journey) return;
    try {
      await deleteJourney(journey.id);
      setAllJourneys(prev => prev.filter(j => j.id !== journey.id));
      setJourney(null);
      setLetters([]);
      setYearReviewText(null);
      setConfirmingReset(false);
      setView('home');
    } catch (err) {
      setErrorStage(null);
      setGenErrorMsg(err.message || 'Could not reset the year.');
      setConfirmingReset(false);
      setView('gen-error');
    }
  };

  /* NAV */

  const goHome = () => {
    setMessages([]);
    setChatInput('');
    setTargetMonthIdx(null);
    setDisplayedLetter(null);
    setFreshLetterMode(false);
    setGenErrorMsg(null);
    setErrorStage(null);
    setView('home');
  };

  const retryFromError = () => {
    setGenErrorMsg(null);
    if (errorStage === 'themes') return startThemeGeneration();
    if (errorStage === 'letter' && messages.length > 0) return composeLetter(messages);
    if (errorStage === 'review') return openYearReview();
    goHome();
  };

  /* ============================================================
     RENDER
     ============================================================ */

  if (loading) {
    return (
      <section className="sp-tab-content">
        <div className="sp-tab-content__placeholder">Loading your Garden...</div>
      </section>
    );
  }

  if (loadError) {
    return (
      <section className="sp-tab-content">
        <div className="sp-tab-content__placeholder">Could not load Garden: {loadError}</div>
      </section>
    );
  }

  if (view === 'no-skills') {
    return (
      <section className="sp-empty">
        <h1 className="sp-empty__title">Your Garden needs roots.</h1>
        <p className="sp-empty__sub">Add some skills in Pulse first, then come back here to begin your year.</p>
        <div className="sp-empty__actions">
          <button className="sp-btn sp-btn--primary sp-btn--big" onClick={() => onNavigateToTab && onNavigateToTab('pulse')}>
            Go to Pulse
          </button>
        </div>
      </section>
    );
  }

  return (
    <div className="sp-gd">
      <div className="sp-gd__bg" aria-hidden="true" />

      {view === 'home' && !journey && (
        <StartScreen onBegin={() => setView('goal-setting')} />
      )}

      {view === 'home' && journey && (
        <JourneyActiveView
          journey={journey}
          letters={letters}
          allJourneys={allJourneys}
          activeYear={activeYear}
          onSwitchYear={switchToYear}
          currentMonthIdx={currentMonthIdx}
          yearReviewUnlocked={yearReviewUnlocked}
          hasYearReview={!!journey.year_review_text}
          onTendMonth={(idx) => startMonthlyConversation(idx)}
          onReadLetter={(idx, letter) => openLetter(letter)}
          onYearReview={openYearReview}
          onReset={requestReset}
          confirmingReset={confirmingReset}
          onCancelReset={() => setConfirmingReset(false)}
          onConfirmReset={confirmReset}
        />
      )}

      {view === 'goal-setting' && (
        <GoalSettingView
          drafts={goalDrafts}
          onUpdate={updateGoal}
          canProceed={canProceedGoals}
          onCancel={goHome}
          onProceed={startThemeGeneration}
        />
      )}

      {view === 'theme-generation' && (
        <GardenLoading label="Composing your year..." sub="Mapping your goals to twelve months." />
      )}

      {view === 'theme-review' && draftThemes && (
        <ThemeReviewView
          journey={{ year: new Date().getFullYear(), start_month: new Date().getMonth() + 1 }}
          themes={draftThemes}
          onUpdate={updateDraftTheme}
          onBack={() => setView('goal-setting')}
          onLock={lockTheYear}
        />
      )}

      {view === 'monthly-conversation' && journey && targetMonthIdx && (
        <MonthlyConversationView
          journey={journey}
          monthIdx={targetMonthIdx}
          messages={messages}
          input={chatInput}
          setInput={setChatInput}
          isThinking={isThinking}
          onSend={(t) => sendMonthlyMessage(t)}
          onCancel={goHome}
          messagesEndRef={messagesEndRef}
        />
      )}

      {view === 'generating-letter' && (
        <GardenLoading label="Tending your garden..." sub="Reading what you shared, finding the season's pattern." />
      )}

      {view === 'letter-display' && journey && displayedLetter && (
        <LetterDisplayView
          journey={journey}
          letter={displayedLetter}
          fresh={freshLetterMode}
          onDone={goHome}
        />
      )}

      {view === 'year-review-generating' && (
        <GardenLoading label="Composing your year in review..." sub="Reading all twelve letters, finding the deeper story." />
      )}

      {view === 'year-review' && journey && yearReviewText && (
        <YearReviewView
          journey={journey}
          reviewText={yearReviewText}
          onDone={goHome}
        />
      )}

      {view === 'gen-error' && (
        <ErrorView
          message={genErrorMsg}
          stage={errorStage}
          onBack={goHome}
          onRetry={retryFromError}
        />
      )}
    </div>
  );
}

/* ============================================================
   START SCREEN (no journey yet)
   ============================================================ */

function StartScreen({ onBegin }) {
  return (
    <section className="sp-empty">
      <h1 className="sp-empty__title sp-gd-h">A year, one month at a time.</h1>
      <p className="sp-empty__sub">
        Set your goals, receive twelve themed months, and tend a letter at the end of each one.
        After all twelve, your year is woven into a single review you'll keep.
      </p>
      <div className="sp-empty__actions">
        <button className="sp-btn sp-gd-btn--primary sp-btn--big" onClick={onBegin}>
          Start your year
        </button>
      </div>
    </section>
  );
}

/* ============================================================
   GOAL SETTING
   ============================================================ */

function GoalSettingView({ drafts, onUpdate, canProceed, onCancel, onProceed }) {
  return (
    <section className="sp-gd-panel">
      <div className="sp-gd-panel__head">
        <h2 className="sp-gd-h">What are you tending this year?</h2>
        <p className="sp-gd-sub">Set up to three goals — at least one. They'll shape every monthly theme.</p>
      </div>

      <div className="sp-gd-goals">
        {drafts.map((draft, i) => {
          const meta = GOAL_TYPE_BY_VALUE[draft.type] || GOAL_TYPES[0];
          return (
            <div key={i} className="sp-gd-goal-card">
              <div className="sp-gd-goal-card__head">
                <span className="sp-gd-goal-card__type">{meta.label}</span>
              </div>
              <input
                type="text"
                value={draft.title}
                onChange={(e) => onUpdate(i, 'title', e.target.value)}
                placeholder={meta.placeholder}
                className="sp-gd-input"
                maxLength={120}
              />
              <textarea
                value={draft.description}
                onChange={(e) => onUpdate(i, 'description', e.target.value)}
                placeholder="Add a sentence or two of context (optional)..."
                rows={2}
                className="sp-gd-input sp-gd-input--textarea"
                maxLength={400}
              />
            </div>
          );
        })}
      </div>

      {!canProceed && (
        <p className="sp-gd-inline-error">Add at least one goal to continue.</p>
      )}

      <div className="sp-gd-panel__actions">
        <button className="sp-btn sp-btn--ghost" onClick={onCancel}>Cancel</button>
        <button
          className="sp-btn sp-gd-btn--primary"
          disabled={!canProceed}
          onClick={onProceed}
        >
          Generate Themes →
        </button>
      </div>
    </section>
  );
}

/* ============================================================
   THEME REVIEW
   ============================================================ */

function ThemeReviewView({ journey, themes, onUpdate, onBack, onLock }) {
  return (
    <section className="sp-gd-panel">
      <div className="sp-gd-panel__head">
        <h2 className="sp-gd-h">Your twelve months</h2>
        <p className="sp-gd-sub">Edit any theme that doesn't feel right. Lock the year when you're ready.</p>
      </div>

      <div className="sp-gd-themes">
        {Array.from({ length: 12 }, (_, i) => i + 1).map(idx => {
          const t = themes[idx] || { theme: '', description: '' };
          return (
            <div key={idx} className="sp-gd-theme-row">
              <div className="sp-gd-theme-row__num">
                <span className="sp-gd-theme-row__month">{monthShortLabel(journey, idx)}</span>
                <span className="sp-gd-theme-row__idx">{String(idx).padStart(2, '0')}</span>
              </div>
              <div className="sp-gd-theme-row__fields">
                <input
                  type="text"
                  value={t.theme}
                  onChange={(e) => onUpdate(idx, 'theme', e.target.value)}
                  className="sp-gd-input sp-gd-input--theme"
                  maxLength={50}
                />
                <input
                  type="text"
                  value={t.description}
                  onChange={(e) => onUpdate(idx, 'description', e.target.value)}
                  className="sp-gd-input sp-gd-input--theme-desc"
                  maxLength={200}
                  placeholder="A sentence to anchor this month..."
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="sp-gd-panel__actions">
        <button className="sp-btn sp-btn--ghost" onClick={onBack}>← Back</button>
        <button className="sp-btn sp-gd-btn--primary" onClick={onLock}>
          Lock the Year →
        </button>
      </div>
    </section>
  );
}

/* ============================================================
   JOURNEY ACTIVE
   ============================================================ */

function JourneyActiveView({
  journey, letters, allJourneys, activeYear, onSwitchYear,
  currentMonthIdx, yearReviewUnlocked, hasYearReview,
  onTendMonth, onReadLetter,
  onYearReview, onReset,
  confirmingReset, onCancelReset, onConfirmReset,
}) {
  const goals = journey.goals || [];
  const themes = journey.themes || {};
  return (
    <div className="sp-gd-active">
      <header className="sp-gd-active__head">
        <div>
          <h1 className="sp-gd-h sp-gd-h--lg">Year of Growth — {journey.year}</h1>
          <p className="sp-gd-sub">{monthLabel(journey, 1)} → {monthLabel(journey, 12)}</p>
        </div>
        <div className="sp-gd-active__head-actions">
          {allJourneys.length > 1 && (
            <select
              value={activeYear}
              onChange={(e) => onSwitchYear(Number(e.target.value))}
              className="sp-gd-year-select"
            >
              {allJourneys.map(j => (
                <option key={j.id} value={j.year}>{j.year}</option>
              ))}
            </select>
          )}
          <button className="sp-gd-link" onClick={onReset}>Reset Year</button>
        </div>
      </header>

      <GoalsRow goals={goals} />

      <VineTimeline
        journey={journey}
        letters={letters}
        currentMonthIdx={currentMonthIdx}
        themes={themes}
        onTendMonth={onTendMonth}
        onReadLetter={onReadLetter}
      />

      {currentMonthIdx <= 12 && (
        <CurrentMonthCTA
          journey={journey}
          monthIdx={currentMonthIdx}
          letters={letters}
          themes={themes}
          onTend={() => onTendMonth(currentMonthIdx)}
        />
      )}

      <LettersList
        journey={journey}
        letters={letters}
        themes={themes}
        onRead={(letter) => onReadLetter(letter.month_index, letter)}
      />

      <div className="sp-gd-yearreview-cta">
        <button
          className={'sp-btn sp-gd-btn--primary sp-btn--big' + (yearReviewUnlocked || hasYearReview ? '' : ' sp-btn--disabled')}
          onClick={onYearReview}
          disabled={!yearReviewUnlocked && !hasYearReview}
        >
          {hasYearReview ? 'Read Year in Review' : 'Generate Year in Review'}
        </button>
        {!yearReviewUnlocked && !hasYearReview && (
          <p className="sp-gd-yearreview-hint">
            Unlocks after all 12 months are tended ({letters.length} of 12 done).
          </p>
        )}
      </div>

      {confirmingReset && (
        <div className="sp-gd-modal-overlay" onClick={onCancelReset}>
          <div className="sp-gd-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="sp-gd-modal__title">Delete your {journey.year} journey?</h3>
            <p className="sp-gd-modal__body">
              This will permanently delete your year and all{' '}
              <strong>{letters.length}</strong>{' '}
              {letters.length === 1 ? 'letter' : 'letters'} you've written. This cannot be undone.
            </p>
            <div className="sp-gd-modal__actions">
              <button className="sp-btn sp-btn--ghost" onClick={onCancelReset}>Cancel</button>
              <button className="sp-btn sp-gd-btn--danger" onClick={onConfirmReset}>
                Delete journey
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GoalsRow({ goals }) {
  if (!goals || goals.length === 0) return null;
  return (
    <div className="sp-gd-goals-row">
      {goals.map((g, i) => {
        const meta = GOAL_TYPE_BY_VALUE[g.type] || GOAL_TYPES[0];
        return (
          <div key={i} className="sp-gd-goal-display">
            <div className="sp-gd-goal-display__head">
              <span className="sp-gd-goal-display__type">{meta.label}</span>
            </div>
            <div className="sp-gd-goal-display__title">{g.title}</div>
            {g.description && <p className="sp-gd-goal-display__desc">{g.description}</p>}
          </div>
        );
      })}
    </div>
  );
}

/* ============================================================
   VINE TIMELINE (SVG)
   ============================================================ */

function VineTimeline({ journey, letters, currentMonthIdx, themes, onTendMonth, onReadLetter }) {
  return (
    <div className="sp-gd-timeline">
      <div className="sp-gd-timeline__scroll">
        <svg viewBox="0 0 1320 180" className="sp-gd-timeline__svg" preserveAspectRatio="xMidYMid meet">
          <path
            d="M 60 60 Q 120 40 180 60 T 300 60 T 420 60 T 540 60 T 660 60 T 780 60 T 900 60 T 1020 60 T 1140 60 T 1260 60"
            stroke="rgba(107, 79, 58, 0.6)"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          />
          {Array.from({ length: 12 }, (_, i) => i + 1).map(idx => {
            const status = letterStatusForMonth(idx, currentMonthIdx, letters);
            const themeData = themes[idx] || themes[String(idx)] || { theme: '', description: '' };
            const letter = (letters || []).find(l => l.month_index === idx);
            const cx = 60 + (idx - 1) * 110;
            const isCurrent = status === 'current';
            const isTended = status === 'tended';
            const isUntended = status === 'untended';
            const isFuture = status === 'future';
            const clickable = !isFuture;
            return (
              <g key={idx} transform={`translate(${cx}, 60)`} style={{ cursor: clickable ? 'pointer' : 'default' }}
                 onClick={() => {
                   if (isFuture) return;
                   if (isTended && letter) onReadLetter(idx, letter);
                   else if (clickable) onTendMonth(idx);
                 }}>
                {isCurrent && (
                  <circle r="22" className="sp-gd-node-pulse" />
                )}
                <circle
                  r={isCurrent ? 13 : 9}
                  fill={isTended || isCurrent ? '#d4a574' : 'transparent'}
                  stroke={isFuture ? 'rgba(107, 79, 58, 0.35)' : '#6b4f3a'}
                  strokeWidth="2"
                  className={'sp-gd-node sp-gd-node--' + status}
                />
                {isTended && (
                  <circle r="3.5" fill="#1a2520" />
                )}
                {isUntended && (
                  <text x="0" y="3" textAnchor="middle" fontSize="10" fill="#6b4f3a">·</text>
                )}
                <text
                  x="0"
                  y="32"
                  textAnchor="middle"
                  fontSize="10"
                  fontFamily="'JetBrains Mono', Monaco, Consolas, monospace"
                  fill={isFuture ? 'rgba(212, 165, 116, 0.4)' : '#d4a574'}
                  letterSpacing="2"
                >
                  {monthShortLabel(journey, idx).toUpperCase()}
                </text>
                <text
                  x="0"
                  y="52"
                  textAnchor="middle"
                  fontSize="11"
                  fontFamily="'Lora', Georgia, serif"
                  fontStyle="italic"
                  fill={isFuture ? 'rgba(245, 230, 211, 0.3)' : 'rgba(245, 230, 211, 0.85)'}
                >
                  {(themeData.theme || '').slice(0, 14)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

/* ============================================================
   CURRENT MONTH CTA
   ============================================================ */

function CurrentMonthCTA({ journey, monthIdx, letters, themes, onTend }) {
  const themeData = themes[monthIdx] || themes[String(monthIdx)] || { theme: '', description: '' };
  const tended = letters.some(l => l.month_index === monthIdx);
  if (tended) {
    return (
      <div className="sp-gd-current">
        <div className="sp-gd-current__label">This month</div>
        <h3 className="sp-gd-current__theme">{themeData.theme}</h3>
        <p className="sp-gd-current__desc">{themeData.description}</p>
        <p className="sp-gd-current__note">Tended. Letter ready in your monthly letters below.</p>
      </div>
    );
  }
  return (
    <div className="sp-gd-current sp-gd-current--active">
      <div className="sp-gd-current__label">{monthLabel(journey, monthIdx)} · Theme</div>
      <h3 className="sp-gd-current__theme">{themeData.theme}</h3>
      <p className="sp-gd-current__desc">{themeData.description}</p>
      <button className="sp-btn sp-gd-btn--primary" onClick={onTend}>
        Tend Your Garden →
      </button>
    </div>
  );
}

/* ============================================================
   LETTERS LIST
   ============================================================ */

function LettersList({ journey, letters, themes, onRead }) {
  if (!letters || letters.length === 0) {
    return (
      <div className="sp-gd-letters">
        <h3 className="sp-gd-letters__title">Monthly Letters</h3>
        <p className="sp-gd-letters__empty">No letters yet. Tend your first month above.</p>
      </div>
    );
  }
  return (
    <div className="sp-gd-letters">
      <h3 className="sp-gd-letters__title">Monthly Letters</h3>
      <div className="sp-gd-letters__list">
        {letters.map(letter => {
          const themeData = themes[letter.month_index] || themes[String(letter.month_index)] || {};
          return (
            <button
              key={letter.id}
              className="sp-gd-letter-card"
              onClick={() => onRead(letter)}
            >
              <span className="sp-gd-letter-card__corner" aria-hidden="true" />
              <div className="sp-gd-letter-card__head">
                <span className="sp-gd-letter-card__num">Month {String(letter.month_index).padStart(2, '0')}</span>
                <span className="sp-gd-letter-card__date">{monthLabel(journey, letter.month_index)}</span>
              </div>
              <div className="sp-gd-letter-card__theme">{themeData.theme}</div>
              <p className="sp-gd-letter-card__preview">
                {String(letter.letter_content || '').slice(0, 220)}{letter.letter_content && letter.letter_content.length > 220 ? '...' : ''}
              </p>
              <div className="sp-gd-letter-card__open">Read letter →</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   MONTHLY CONVERSATION
   ============================================================ */

function MonthlyConversationView({ journey, monthIdx, messages, input, setInput, isThinking, onSend, onCancel, messagesEndRef }) {
  const userTurns = messages.filter(m => m.role === 'user').length;
  const themeData = (journey.themes && (journey.themes[monthIdx] || journey.themes[String(monthIdx)])) || { theme: '', description: '' };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim() && !isThinking) onSend(input.trim());
  };
  return (
    <div className="sp-gd-chat">
      <div className="sp-gd-chat__header">
        <button className="sp-gd-chat__back" onClick={onCancel}>← Cancel</button>
        <div className="sp-gd-chat__title">
          <span className="sp-gd-chat__leaf" aria-hidden="true" />
          {monthLabel(journey, monthIdx).toUpperCase()} · {(themeData.theme || '').toUpperCase()}
        </div>
        <div className="sp-gd-chat__count">{userTurns} of ~5</div>
      </div>

      <div className="sp-gd-chat__messages">
        {messages.map((msg) => (
          <div key={msg.id} className={`sp-gd-msg sp-gd-msg--${msg.role}`}>
            {msg.role === 'assistant' && <div className="sp-gd-msg__avatar" aria-hidden="true">G</div>}
            <div className="sp-gd-msg__bubble">{msg.content}</div>
          </div>
        ))}
        {isThinking && (
          <div className="sp-gd-msg sp-gd-msg--assistant">
            <div className="sp-gd-msg__avatar" aria-hidden="true">G</div>
            <div className="sp-gd-msg__bubble sp-gd-msg__bubble--thinking">
              <span className="sp-gd-dot" />
              <span className="sp-gd-dot" />
              <span className="sp-gd-dot" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="sp-gd-input" onSubmit={handleSubmit}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          placeholder={isThinking ? 'Garden is reflecting...' : 'Speak honestly...'}
          disabled={isThinking}
          className="sp-gd-input__field"
          rows={1}
        />
        <button
          type="submit"
          disabled={isThinking || !input.trim()}
          className="sp-gd-input__send"
          aria-label="Send"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M2 12 L22 2 L18 22 L11 14 L2 12 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          </svg>
        </button>
      </form>
    </div>
  );
}

/* ============================================================
   LOADING (composing)
   ============================================================ */

function GardenLoading({ label, sub }) {
  return (
    <section className="sp-gd-loading">
      <div className="sp-gd-loading__leaf" aria-hidden="true">
        <svg viewBox="0 0 60 60" width="60" height="60">
          <path
            d="M 30 6 C 14 14, 8 30, 14 50 C 30 44, 46 30, 46 12 C 38 14, 32 6, 30 6 Z"
            fill="#8db580"
            opacity="0.85"
          />
          <path
            d="M 30 8 Q 28 30 18 48"
            stroke="#2a3d35"
            strokeWidth="1"
            fill="none"
          />
        </svg>
      </div>
      <p className="sp-gd-loading__text">{label}</p>
      <p className="sp-gd-loading__sub">{sub}</p>
    </section>
  );
}

/* ============================================================
   LETTER DISPLAY
   ============================================================ */

function LetterDisplayView({ journey, letter, fresh, onDone }) {
  const themeData = (journey.themes && (journey.themes[letter.month_index] || journey.themes[String(letter.month_index)])) || { theme: '', description: '' };
  return (
    <div className="sp-gd-letter-result">
      <LetterPaper journey={journey} letter={letter} themeData={themeData} />
      <div className="sp-gd-letter-result__actions">
        <button className="sp-btn sp-btn--ghost" onClick={() => window.print()}>Print Letter</button>
        <button className="sp-btn sp-gd-btn--primary" onClick={onDone}>
          {fresh
            ? (
                <>
                  <span className="sp-btn__check" aria-hidden="true">
                    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 8.5 6.5 12 13 4.5" />
                    </svg>
                  </span>
                  <span>Tended · View Garden</span>
                </>
              )
            : 'Done · View Garden'
          }
        </button>
      </div>
    </div>
  );
}

function LetterPaper({ journey, letter, themeData }) {
  const paragraphs = String(letter.letter_content || '').split(/\n\s*\n/).filter(Boolean);
  return (
    <article className="sp-gd-paper">
      <div className="sp-gd-paper__seal" aria-hidden="true">G</div>
      <div className="sp-gd-paper__date">{formatLetterDate(letter.created_at)}</div>
      <header className="sp-gd-paper__header">
        <div className="sp-gd-paper__month">{monthLabel(journey, letter.month_index)}</div>
        <h1 className="sp-gd-paper__theme">{themeData.theme || `Month ${letter.month_index}`}</h1>
        {themeData.description && (
          <p className="sp-gd-paper__theme-desc">{themeData.description}</p>
        )}
      </header>
      <div className="sp-gd-paper__body">
        {paragraphs.map((p, i) => (
          <p
            key={i}
            className={'sp-gd-paper__p' + (i === 0 ? ' sp-gd-paper__p--first' : '')}
          >
            {p}
          </p>
        ))}
      </div>
      <footer className="sp-gd-paper__footer" aria-hidden="true">
        <span className="sp-gd-paper__pressed-leaf" aria-hidden="true" />
      </footer>
    </article>
  );
}

/* ============================================================
   YEAR REVIEW
   ============================================================ */

function YearReviewView({ journey, reviewText, onDone }) {
  const sections = useMemo(() => parseYearReview(reviewText), [reviewText]);
  const startLabel = monthLabel(journey, 1);
  const endLabel = monthLabel(journey, 12);
  return (
    <div className="sp-gd-review-result">
      <article className="sp-gd-review">
        <div className="sp-gd-review__corner sp-gd-review__corner--tl" aria-hidden="true" />
        <div className="sp-gd-review__corner sp-gd-review__corner--tr" aria-hidden="true" />
        <div className="sp-gd-review__corner sp-gd-review__corner--bl" aria-hidden="true" />
        <div className="sp-gd-review__corner sp-gd-review__corner--br" aria-hidden="true" />

        <header className="sp-gd-review__header">
          <div className="sp-gd-review__eyebrow">YEAR IN REVIEW</div>
          <h1 className="sp-gd-review__title">{journey.year}</h1>
          <p className="sp-gd-review__range">{startLabel} → {endLabel}</p>
        </header>

        {sections.length === 0 ? (
          // Fallback when parser couldn't split — render whole text as prose
          <div className="sp-gd-review__body">
            {String(reviewText).split(/\n\s*\n/).filter(Boolean).map((p, i) => (
              <p key={i} className="sp-gd-review__p">{p}</p>
            ))}
          </div>
        ) : (
          sections.map((s, i) => (
            <section key={i} className="sp-gd-review__section">
              <h2 className="sp-gd-review__h2">{s.title}</h2>
              <div className="sp-gd-review__body">
                {s.body.split(/\n\s*\n/).filter(Boolean).map((p, j) => (
                  <p key={j} className="sp-gd-review__p">{p}</p>
                ))}
              </div>
            </section>
          ))
        )}
      </article>

      <div className="sp-gd-review-result__actions">
        <button className="sp-btn sp-btn--ghost" onClick={() => window.print()}>Print Annual Book</button>
        <button className="sp-btn sp-gd-btn--primary" onClick={onDone}>
          <span className="sp-btn__check" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 8.5 6.5 12 13 4.5" />
            </svg>
          </span>
          <span>Done · View Garden</span>
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   ERROR
   ============================================================ */

function ErrorView({ message, stage, onBack, onRetry }) {
  const label = stage === 'themes' ? 'compose your themes'
              : stage === 'letter' ? 'write the letter'
              : stage === 'review' ? 'compose your year review'
              : 'finish that step';
  return (
    <section className="sp-gd-error">
      <h2 className="sp-gd-error__title">Couldn't {label}.</h2>
      <p className="sp-gd-error__msg">{message}</p>
      <div className="sp-gd-error__actions">
        <button className="sp-btn sp-btn--ghost" onClick={onBack}>Back</button>
        <button className="sp-btn sp-gd-btn--primary" onClick={onRetry}>Try again</button>
      </div>
    </section>
  );
}
